/**
 * 日志管理模块 - 完整的日志查看和管理功能
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Button, Input, Select, Space, Modal, message, DatePicker, Card, Row, Col, Tag, Tooltip, Drawer, Form, Switch, Checkbox } from 'antd';
import { 
  SearchOutlined, 
  ReloadOutlined, 
  DeleteOutlined, 
  ExportOutlined, 
  EyeOutlined,
  ClearOutlined,
  SettingOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { logService } from '../../../../../services/logService';
import { logQueryOptimizer } from '../../../../../utils/logQueryOptimizer';
import { LogRecord, LogQueryParams, LogDetail, LogActionType, LogResult, LogLevel } from '../../../../../types/log';
import { PAGINATION_CONFIG } from '../../components/PaginationConfig';
import QueryArea, { QueryField } from '../../components/QueryArea';
import { useDict } from '../../../../../hooks/useUnifiedDict';
import { DICT_TYPES } from '../../../../../constants/dictTypes';
import moment from 'moment';

const { RangePicker } = DatePicker;
const { TextArea } = Input;

const LogManagement: React.FC = () => {
  // 使用字典翻译模块和操作
  const { getLabelByValue: getModuleLabel, dictData: moduleDictData } = useDict(DICT_TYPES.LOG_MODULE);
  const { getLabelByValue: getActionLabel, dictData: actionDictData } = useDict(DICT_TYPES.LOG_ACTION);
  
  const [dataSource, setDataSource] = useState<LogRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchParams, setSearchParams] = useState<LogQueryParams>({
    keyword: '',
    module: '',
    action: undefined,
    result: undefined,
    level: undefined,
    startTime: '',
    endTime: '',
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedLog, setSelectedLog] = useState<LogDetail | null>(null);
  const [cleanModalVisible, setCleanModalVisible] = useState(false);
  const [cleanDays, setCleanDays] = useState(30);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [logConfig, setLogConfig] = useState({
    enabled: true,
    actions: ['QUERY', 'CREATE', 'UPDATE', 'DELETE', 'LOGIN', 'LOGOUT'],
    levels: ['INFO', 'WARN', 'ERROR'],
  });
  const [configForm] = Form.useForm();

  // 查询字段配置（使用useMemo确保字典数据更新时重新生成）
  const queryFields: QueryField[] = React.useMemo(() => [
    {
      key: 'keyword',
      label: '关键词',
      type: 'input',
      placeholder: '搜索操作描述或用户...',
      width: 200,
    },
    {
      key: 'module',
      label: '模块',
      type: 'select',
      placeholder: '选择模块',
      width: 150,
      // 从字典数据中动态生成选项
      options: moduleDictData ? moduleDictData.map((item: any) => ({
        label: item.dictLabel,
        value: item.dictValue,
      })) : [],
    },
    {
      key: 'action',
      label: '操作',
      type: 'select',
      placeholder: '选择操作',
      width: 120,
      // 从字典数据中动态生成选项
      options: actionDictData ? actionDictData.map((item: any) => ({
        label: item.dictLabel,
        value: item.dictValue,
      })) : [],
    },
    {
      key: 'result',
      label: '结果',
      type: 'select',
      placeholder: '选择结果',
      width: 100,
      options: [
        { label: '成功', value: 'SUCCESS' },
        { label: '失败', value: 'FAILED' },
      ],
    },
    {
      key: 'level',
      label: '级别',
      type: 'select',
      placeholder: '选择级别',
      width: 100,
      options: [
        { label: 'INFO', value: 'INFO' },
        { label: 'WARN', value: 'WARN' },
        { label: 'ERROR', value: 'ERROR' },
      ],
    },
    {
      key: 'timeRange',
      label: '时间范围',
      type: 'dateRange',
      placeholder: '选择时间范围',
      width: 240,
    },
  ], [moduleDictData, actionDictData]);

  // 表格列定义
  const columns: ColumnsType<LogRecord> = [
    {
      title: '用户',
      key: 'user',
      width: 120,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.userName}</div>
          {record.userNickname && (
            <div style={{ fontSize: 12, color: '#666' }}>{record.userNickname}</div>
          )}
        </div>
      ),
    },
    {
      title: '模块',
      dataIndex: 'module',
      key: 'module',
      width: 100,
      render: (value) => {
        const label = getModuleLabel(value) || value;
        return <Tag color="blue">{label}</Tag>;
      },
    },
    {
      title: '操作',
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: (value: LogActionType) => {
        const actionColors: Record<string, string> = {
          [LogActionType.QUERY]: 'default',
          [LogActionType.CREATE]: 'green',
          [LogActionType.UPDATE]: 'blue',
          [LogActionType.DELETE]: 'red',
          [LogActionType.BATCH_DELETE]: 'red',
          [LogActionType.LOGIN]: 'purple',
          [LogActionType.LOGOUT]: 'orange',
          [LogActionType.EXPORT]: 'cyan',
          [LogActionType.IMPORT]: 'magenta',
        };
        const label = getActionLabel(value) || value;
        const color = actionColors[value] || 'default';
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: '目标',
      key: 'target',
      width: 150,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.targetType}</div>
          {record.targetTitle && (
            <div style={{ fontSize: 12, color: '#666' }}>{record.targetTitle}</div>
          )}
        </div>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: '结果',
      dataIndex: 'result',
      key: 'result',
      width: 80,
      align: 'center',
      render: (value: LogResult) => (
        <Tag color={value === LogResult.SUCCESS ? 'green' : 'red'}>
          {value === LogResult.SUCCESS ? '成功' : '失败'}
        </Tag>
      ),
    },
    {
      title: '级别',
      dataIndex: 'level',
      key: 'level',
      width: 80,
      align: 'center',
      render: (value: LogLevel) => {
        const levelColors: Record<LogLevel, string> = {
          [LogLevel.INFO]: 'blue',
          [LogLevel.WARN]: 'orange',
          [LogLevel.ERROR]: 'red',
          [LogLevel.DEBUG]: 'gray',
        };
        return <Tag color={levelColors[value]}>{value}</Tag>;
      },
    },
    {
      title: '执行时间',
      dataIndex: 'executionTime',
      key: 'executionTime',
      width: 100,
      align: 'center',
      render: (value) => value ? `${value}ms` : '-',
    },
    {
      title: 'IP地址',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 120,
    },
    {
      title: '时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 180,
      render: (value) => value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="primary" 
            size="small" 
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record.id!)}
          >
            详情
          </Button>
        </Space>
      ),
    },
  ];

  // 加载数据
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        current,
        size: pageSize,
        ...searchParams,
      };
      
      const result = await logQueryOptimizer.queryLogs(params, true);
      setDataSource(result.data);
      setTotal(result.total);
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [current, pageSize, searchParams]);

  useEffect(() => {
    loadData();
  }, [current, pageSize, searchParams, loadData]);

  // 搜索
  const handleSearch = () => {
    setCurrent(1);
    // 清除缓存，强制重新查询
    logQueryOptimizer.clearCache();
    loadData();
  };

  // 重置
  const handleReset = () => {
    setSearchParams({
      keyword: '',
      module: '',
      action: undefined,
      result: undefined,
      level: undefined,
      startTime: '',
      endTime: '',
    });
    setCurrent(1);
    setTimeout(() => {
      loadData();
    }, 0);
  };

  // 处理查询参数变化
  const handleSearchParamsChange = useCallback((values: Record<string, any>) => {
    const timeRange = values.timeRange;
    setSearchParams({
      keyword: values.keyword || '',
      module: values.module || '',
      action: values.action || undefined,
      result: values.result || undefined,
      level: values.level || undefined,
      startTime: timeRange && timeRange[0] ? timeRange[0].format('YYYY-MM-DD HH:mm:ss') : '',
      endTime: timeRange && timeRange[1] ? timeRange[1].format('YYYY-MM-DD HH:mm:ss') : '',
    });
  }, []);

  // 查看详情
  const handleViewDetail = async (id: number) => {
    try {
      const response = await logService.getDetail(id);
      if (response.code === 200) {
        setSelectedLog(response.data);
        setDetailVisible(true);
      } else {
        message.error(response.message || '获取详情失败');
      }
    } catch (error) {
      console.error('获取详情失败:', error);
      message.error('获取详情失败，请稍后重试');
    }
  };

  // 批量删除
  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的数据');
      return;
    }
    
    Modal.confirm({
      title: '确认批量删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 条日志记录吗？删除后无法恢复。`,
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        setLoading(true);
        try {
          const response = await logService.batchDelete(selectedRowKeys as number[]);
          if (response.code === 200) {
            message.success('批量删除成功');
            setSelectedRowKeys([]);
            loadData();
          } else {
            message.error(response.message || '批量删除失败');
          }
        } catch (error) {
          console.error('批量删除失败:', error);
          message.error('批量删除失败，请稍后重试');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // 导出日志
  const handleExport = async () => {
    try {
      const response = await logService.exportLogs(searchParams);
      // 确保response.data是实际的文件数据
      const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `日志记录_${moment().format('YYYY-MM-DD')}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      message.success('导出成功');
    } catch (error) {
      console.error('导出失败:', error);
      message.error('导出失败，请稍后重试');
    }
  };

  // 清理过期日志
  const handleCleanExpired = async () => {
    setLoading(true);
    try {
      const response = await logService.cleanExpiredLogs(cleanDays);
      if (response.code === 200) {
        message.success(`成功清理${cleanDays}天前的日志记录`);
        setCleanModalVisible(false);
        loadData();
      } else {
        message.error(response.message || '清理失败');
      }
    } catch (error) {
      console.error('清理失败:', error);
      message.error('清理失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 加载日志配置
  const loadLogConfig = async () => {
    try {
      const response = await logService.getConfig();
      if (response.code === 200 && response.data) {
        const config = response.data;
        setLogConfig({
          enabled: config.enabled !== false,
          actions: config.actions || ['QUERY', 'CREATE', 'UPDATE', 'DELETE', 'LOGIN', 'LOGOUT'],
          levels: config.levels || ['INFO', 'WARN', 'ERROR'],
        });
        configForm.setFieldsValue({
          enabled: config.enabled !== false,
          actions: config.actions || ['QUERY', 'CREATE', 'UPDATE', 'DELETE', 'LOGIN', 'LOGOUT'],
        });
      }
    } catch (error) {
      console.error('加载日志配置失败:', error);
      // 使用默认配置
      configForm.setFieldsValue({
        enabled: true,
        actions: ['QUERY', 'CREATE', 'UPDATE', 'DELETE', 'LOGIN', 'LOGOUT'],
      });
    }
  };

  // 保存日志配置
  const handleSaveConfig = async () => {
    try {
      const values = await configForm.validateFields();
      const config = {
        enabled: values.enabled,
        actions: values.actions || [],
        // 始终使用所有日志级别，不在UI中显示但后端需要
        levels: ['INFO', 'WARN', 'ERROR'],
      };
      
      const response = await logService.updateConfig(config);
      if (response.code === 200) {
        message.success('日志配置保存成功');
        setConfigModalVisible(false);
        setLogConfig(config);
      } else {
        message.error(response.message || '保存配置失败');
      }
    } catch (error) {
      console.error('保存配置失败:', error);
      message.error('保存配置失败，请稍后重试');
    }
  };

  return (
    <div className="log-management">

      {/* 统一查询区域 */}
      <QueryArea
        fields={queryFields}
        values={{
                    ...searchParams,
          timeRange: searchParams.startTime && searchParams.endTime ? [
            moment(searchParams.startTime),
            moment(searchParams.endTime)
          ] : null,
        }}
        onValuesChange={handleSearchParamsChange}
        onAdd={handleExport}
        onBatchDelete={() => setCleanModalVisible(true)}
        batchDeleteDisabled={false}
        selectedCount={0}
        addButtonText="导出"
        batchDeleteButtonText="清理过期日志"
        customButtons={[
          {
            key: 'config',
            text: '日志配置',
            icon: <SettingOutlined />,
            type: 'default',
            onClick: () => {
              loadLogConfig();
              setConfigModalVisible(true);
            }
          }
        ]}
      />

      {/* 数据表格 */}
      <div className="table-card">
        <Table
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          pagination={{
            current,
            pageSize,
            total,
            ...PAGINATION_CONFIG.default,
            onChange: (page, size) => {
              setCurrent(page);
              setPageSize(size);
            },
          }}
          scroll={{ x: 1500 }}
        />
      </div>

      {/* 日志详情抽屉 */}
      <Drawer
        title="日志详情"
        placement="right"
        width={800}
        open={detailVisible}
        onClose={() => setDetailVisible(false)}
      >
        {selectedLog && (
          <div>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Card size="small" title="基本信息">
                  <p><strong>用户：</strong>{selectedLog.userName} ({selectedLog.userNickname})</p>
                  <p><strong>模块：</strong>{selectedLog.module}</p>
                  <p><strong>操作：</strong>{selectedLog.action}</p>
                  <p><strong>目标：</strong>{selectedLog.targetType}</p>
                  <p><strong>结果：</strong>{selectedLog.result}</p>
                  <p><strong>级别：</strong>{selectedLog.level}</p>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="技术信息">
                  <p><strong>IP地址：</strong>{selectedLog.ipAddress}</p>
                  <p><strong>请求方法：</strong>{selectedLog.requestMethod}</p>
                  <p><strong>请求URL：</strong>{selectedLog.requestUrl}</p>
                  <p><strong>执行时间：</strong>{selectedLog.executionTime}ms</p>
                  <p><strong>创建时间：</strong>{moment(selectedLog.createTime).format('YYYY-MM-DD HH:mm:ss')}</p>
                </Card>
              </Col>
            </Row>
            
            <Card size="small" title="操作描述" style={{ marginBottom: 16 }}>
              <p>{selectedLog.description}</p>
            </Card>

            {selectedLog.requestParams && (
              <Card size="small" title="请求参数" style={{ marginBottom: 16 }}>
                <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 4 }}>
                  {(() => {
                    try {
                      const parsed = JSON.parse(selectedLog.requestParams);
                      return JSON.stringify(parsed, null, 2);
                    } catch (error) {
                      return selectedLog.requestParams;
                    }
                  })()}
                </pre>
              </Card>
            )}

            {selectedLog.responseData && (
              <Card size="small" title="响应数据" style={{ marginBottom: 16 }}>
                <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 4 }}>
                  {(() => {
                    try {
                      const parsed = JSON.parse(selectedLog.responseData);
                      return JSON.stringify(parsed, null, 2);
                    } catch (error) {
                      return selectedLog.responseData;
                    }
                  })()}
                </pre>
              </Card>
            )}

            {selectedLog.errorMessage && (
              <Card size="small" title="错误信息" style={{ marginBottom: 16 }}>
                <TextArea
                  value={selectedLog.errorMessage}
                  rows={4}
                  readOnly
                  style={{ background: '#fff2f0', borderColor: '#ffccc7' }}
                />
              </Card>
            )}
          </div>
        )}
      </Drawer>

      {/* 清理过期日志模态框 */}
      <Modal
        title="清理过期日志"
        open={cleanModalVisible}
        onOk={handleCleanExpired}
        onCancel={() => setCleanModalVisible(false)}
        okText="确定"
        cancelText="取消"
        confirmLoading={loading}
      >
        <p>确定要清理 {cleanDays} 天前的日志记录吗？</p>
        <p style={{ color: '#666', fontSize: 12 }}>
          此操作不可恢复，建议在业务低峰期执行。
        </p>
        <div style={{ marginTop: 16 }}>
          <label>清理天数：</label>
          <Input
            type="number"
            value={cleanDays}
            onChange={(e) => setCleanDays(Number(e.target.value))}
            style={{ width: 100, marginLeft: 8 }}
            min={1}
            max={365}
          />
        </div>
      </Modal>

      {/* 日志配置模态框 */}
      <Modal
        title="日志配置"
        open={configModalVisible}
        onOk={handleSaveConfig}
        onCancel={() => setConfigModalVisible(false)}
        okText="保存"
        cancelText="取消"
        width={600}
      >
        <Form
          form={configForm}
          layout="vertical"
          initialValues={{
            enabled: true,
            actions: ['QUERY', 'CREATE', 'UPDATE', 'DELETE', 'LOGIN', 'LOGOUT'],
          }}
        >
          <Form.Item
            name="enabled"
            label="日志总开关"
            valuePropName="checked"
            tooltip="关闭后，系统将不再记录任何日志"
          >
            <Switch
              checkedChildren="开启"
              unCheckedChildren="关闭"
            />
          </Form.Item>

          <Form.Item
            name="actions"
            label="记录的操作类型"
            tooltip="选择需要记录日志的操作类型，未选中的操作将不会被记录"
            rules={[{ required: true, message: '请至少选择一种操作类型' }]}
          >
            <Checkbox.Group>
              <Space direction="vertical">
                <Checkbox value="QUERY">查询</Checkbox>
                <Checkbox value="CREATE">新增</Checkbox>
                <Checkbox value="UPDATE">修改</Checkbox>
                <Checkbox value="DELETE">删除</Checkbox>
                <Checkbox value="LOGIN">登录</Checkbox>
                <Checkbox value="LOGOUT">登出</Checkbox>
              </Space>
            </Checkbox.Group>
          </Form.Item>

          <div style={{ marginTop: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
            <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
              <strong>说明：</strong>
            </p>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: 20, fontSize: 12, color: '#666' }}>
              <li>关闭日志总开关后，系统将完全停止日志记录</li>
              <li>操作类型的配置只有在日志总开关开启时才会生效</li>
              <li>配置修改后立即生效，无需重启系统</li>
            </ul>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default LogManagement;
